import React, { useEffect, useState, useCallback } from 'react';
import {
  ButtonPrimary,
  ButtonSecondary,
  cssColorValue,
  semanticColors,
  spacer24,
  spacer32,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import analytics from 'utils/client/analytics/analytics';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';

interface FeedbackWidgetProps {
  articleIdentifier: string;
  ubi: UbiArticlePage;
}

const Container = styled.div`
  border-top: 2px solid ${cssColorValue(semanticColors.decorativeSubdued)};
  padding-top: ${spacer32};
  min-height: 140px;
  margin-block-start: ${spacer24};
  padding: ${spacer8};
`;

const FeedbackWidget = ({ articleIdentifier, ubi }: FeedbackWidgetProps) => {
  const { t } = useTranslation();
  const [answered, setAnswered] = useState<string>();

  useEffect(() => {
    ubi.logArticleHelpfulImpression();
  }, [ubi]);

  const vote = useCallback(
    (helpful: boolean) => {
      analytics.trackEvent({
        category: 'article-feedback',
        action: helpful ? 'yes' : 'no',
        label: `/article/${articleIdentifier}/`,
        nonInteraction: true,
      });

      if (helpful) {
        ubi.logArticleHelpfulYes();
      } else {
        ubi.logArticleHelpfulNo();
      }

      setAnswered(helpful ? 'yes' : 'no');
    },
    [articleIdentifier, ubi],
  );

  const renderButtons = () => {
    if (answered) {
      return (
        <Type role="alert" as="p">
          {t('feedback_widget_thanks')}
        </Type>
      );
    }

    return (
      <>
        <ButtonSecondary buttonSize={ButtonPrimary.sm} onClick={() => vote(true)}>
          {t('yes')}
        </ButtonSecondary>{' '}
        <ButtonSecondary buttonSize={ButtonPrimary.sm} onClick={() => vote(false)}>
          {t('no')}
        </ButtonSecondary>
      </>
    );
  };

  return (
    <Container>
      <Type as="p" paddingBottom="1em">
        <strong>{t('feedback_widget_was_this_helpful')}</strong>
      </Type>
      {renderButtons()}
    </Container>
  );
};

export default FeedbackWidget;
